/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Users, Hash, Globe, Lock, Compass, Loader, Plus } from 'lucide-react';
import { communitiesAPI } from '../utils/api';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import './ExplorePage.css';

const CATEGORIES = [
  { key:'',           label:'All',       emoji:'🌐' },
  { key:'technology', label:'Tech',      emoji:'💻' },
  { key:'education',  label:'Education', emoji:'📚' },
  { key:'gaming',     label:'Gaming',    emoji:'🎮' },
  { key:'art',        label:'Art',       emoji:'🎨' },
  { key:'music',      label:'Music',     emoji:'🎵' },
  { key:'business',   label:'Business',  emoji:'💼' },
  { key:'health',     label:'Health',    emoji:'🏃' },
  { key:'science',    label:'Science',   emoji:'🔬' },
  { key:'other',      label:'Other',     emoji:'✨' },
];

function CreateCommunityModal({ onClose, onCreate }) {
  const [form, setForm] = useState({ name:'', handle:'', description:'', category:'technology', type:'public' });
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  const handleCreate = async () => {
    if (!form.name.trim() || !form.handle.trim()) { toast.error('Name and handle required'); return; }
    if (!/^[a-z0-9_]+$/.test(form.handle)) { toast.error('Handle: lowercase letters, numbers, _ only'); return; }
    setLoading(true);
    try {
      const { data } = await communitiesAPI.create(form);
      onCreate(data);
      onClose();
      toast.success(`Community "${form.name}" created!`);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to create'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e=>e.stopPropagation()}>
        <div className="modal-header">
          <h2>Create Community</h2>
          <button className="btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="form-group">
          <label>Community Name *</label>
          <input placeholder="e.g. Python Developers" value={form.name}
            onChange={e => setForm(p => ({ ...p, name: e.target.value, handle: e.target.value.toLowerCase().replace(/\s+/g,'_').replace(/[^a-z0-9_]/g,'').slice(0,30) }))}/>
        </div>
        <div className="form-group">
          <label>Handle *</label>
          <div style={{position:'relative'}}>
            <span style={{position:'absolute',left:12,top:'50%',transform:'translateY(-50%)',color:'var(--text-muted)'}}>@</span>
            <input style={{paddingLeft:26}} placeholder="python_devs" value={form.handle}
              onChange={e => setForm(p => ({ ...p, handle: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g,'') }))}/>
          </div>
        </div>
        <div className="form-group">
          <label>Description</label>
          <textarea rows={3} placeholder="What is this community about?" value={form.description}
            onChange={e => setForm(p => ({ ...p, description: e.target.value }))} maxLength={500}/>
        </div>
        <div className="form-group">
          <label>Category</label>
          <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}>
            {CATEGORIES.filter(c=>c.key).map(c => <option key={c.key} value={c.key}>{c.emoji} {c.label}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label>Privacy</label>
          <div style={{display:'flex',gap:10}}>
            {[{v:'public',l:'🌐 Public'},{v:'private',l:'🔒 Private'}].map(opt => (
              <button key={opt.v}
                className={`btn ${form.type===opt.v?'btn-primary':'btn-secondary'}`}
                style={{flex:1}} onClick={()=>setForm(p=>({...p,type:opt.v}))}>
                {opt.l}
              </button>
            ))}
          </div>
        </div>
        <div style={{display:'flex',gap:10,justifyContent:'flex-end',marginTop:8}}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleCreate} disabled={loading}>
            {loading ? <span className="loader" style={{width:15,height:15,borderWidth:2}}/> : '🚀 Create Community'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ExploreCommunitiesPage() {
  const [communities, setCommunities] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [search, setSearch]           = useState('');
  const [category, setCategory]       = useState('');
  const [joining, setJoining]         = useState(null);
  const [showCreate, setShowCreate]   = useState(false);
  const navigate = useNavigate();
  const toast    = useToast();
  const { user } = useAuth();

  useEffect(() => {
    communitiesAPI.getAll()
      .then(({ data }) => setCommunities(data))
      .catch(() => toast.error('Could not load communities'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = communities.filter(c => {
    const matchSearch = !search ||
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.description?.toLowerCase().includes(search.toLowerCase()) ||
      c.handle?.toLowerCase().includes(search.toLowerCase());
    const matchCat = !category || c.category === category;
    return matchSearch && matchCat;
  });

  const handleJoin = async (communityId) => {
    setJoining(communityId);
    try {
      await communitiesAPI.join(communityId);
      toast.success('Joined! Welcome 🎉');
      navigate(`/communities/${communityId}`);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to join'); }
    finally { setJoining(null); }
  };

  return (
    <div className="explore-page">
      <div className="explore-header">
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:6}}>
          <div style={{display:'flex',alignItems:'center',gap:10}}>
            <Compass size={22} style={{color:'var(--primary-light)'}}/>
            <h1 className="explore-title">Explore Communities</h1>
          </div>
          <button className="btn btn-primary btn-sm" onClick={()=>setShowCreate(true)}>
            <Plus size={15}/> Create
          </button>
        </div>
        <p style={{color:'var(--text-muted)',fontSize:'0.85rem',marginBottom:16}}>
          Join communities for every interest — tech, gaming, art, education and more
        </p>

        <div className="explore-search">
          <Search size={17}/>
          <input placeholder="Search communities..." value={search} onChange={e=>setSearch(e.target.value)}/>
          {search && <button className="btn-icon" style={{padding:4}} onClick={()=>setSearch('')}>✕</button>}
        </div>

        <div className="category-filters">
          {CATEGORIES.map(cat => (
            <button key={cat.key}
              className={`category-filter-btn ${category===cat.key?'active':''}`}
              onClick={()=>setCategory(cat.key)}>
              {cat.emoji} {cat.label}
            </button>
          ))}
        </div>
      </div>

      <div className="explore-grid-container">
        {loading ? (
          <div className="empty-state"><div className="loader"/></div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <Compass size={48}/>
            <h3>No communities found</h3>
            <p>Try a different search or be the first to create one!</p>
            <button className="btn btn-primary" onClick={()=>setShowCreate(true)}>
              <Plus size={15}/> Create Community
            </button>
          </div>
        ) : (
          <div className="communities-grid">
            {filtered.map(comm => {
              const cId  = comm._id || comm.id;
              const hue  = Math.abs(cId.split('').reduce((a,c)=>a+c.charCodeAt(0),0) * 17) % 360;
              const isMember = comm.members?.includes(user?._id || user?.id);
              return (
                <div key={cId} className="community-card">
                  <div className="community-card-banner"
                    style={{background:`linear-gradient(135deg,hsl(${hue},55%,20%),hsl(${(hue+50)%360},45%,15%))`}}>
                    <div className="community-card-icon">
                      <span style={{fontSize:'1.4rem',fontWeight:700}}>
                        {comm.name.slice(0,2).toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div className="community-card-body">
                    <div className="community-card-header">
                      <h3>{comm.name}</h3>
                      {comm.type==='private' ? <Lock size={13}/> : <Globe size={13} style={{color:'var(--accent-green)'}}/>}
                    </div>
                    <p className="community-card-handle">@{comm.handle}</p>
                    <p className="community-card-desc">{comm.description||'No description yet.'}</p>
                    <div className="community-card-meta">
                      <span><Users size={12}/> {(comm.memberCount||0).toLocaleString()} members</span>
                      <span><Hash size={12}/> {comm.channels?.length||0} channels</span>
                    </div>
                    <div className="community-card-actions">
                      {isMember ? (
                        <button className="btn btn-secondary" style={{flex:1}}
                          onClick={()=>navigate(`/communities/${cId}`)}>
                          Open
                        </button>
                      ) : (
                        <button className="btn btn-primary" style={{flex:1}}
                          onClick={()=>handleJoin(cId)} disabled={joining===cId}>
                          {joining===cId ? <Loader size={14} className="animate-spin"/> : 'Join'}
                        </button>
                      )}
                      <button className="btn btn-secondary" style={{padding:'8px 12px'}}
                        onClick={()=>navigate(`/communities/${cId}`)}>
                        Preview
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showCreate && (
        <CreateCommunityModal
          onClose={()=>setShowCreate(false)}
          onCreate={(c)=>setCommunities(prev=>[c,...prev])}
        />
      )}
    </div>
  );
}